/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick, logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import styled from 'styled-components';

const messages = defineMessages({
    or: { id: 'languageStudio.callCenterNextStepsGetStarted.or', defaultMessage: 'or' },
    signInAzure: { id: 'languageStudio.callCenterNextStepsGetStarted.signInAzure', defaultMessage: 'Sign in with Azure' },
    speechResource: { id: 'languageStudio.callCenterNextStepsGetStarted.speechResource', defaultMessage: 'Speech resource' },
    viewSamples: { id: 'languageStudio.callCenterNextStepsGetStarted.viewSamples', defaultMessage: 'View samples on GitHub' },
    quickStart: { id: 'languageStudio.callCenterNextStepsGetStarted.quickStart', defaultMessage: '2. Follow the quick start' },
    createResource: { id: 'languageStudio.callCenterNextStepsGetStarted.createResource', defaultMessage: '1. Create resources' },
    openQuickStart: { id: 'languageStudio.callCenterNextStepsGetStarted.openQuickStart', defaultMessage: 'Open the quick start' },
    signUpAzure: { id: 'languageStudio.callCenterNextStepsGetStarted.signUpAzure', defaultMessage: 'Sign up for an Azure account' },
    cognitiveResource: {
        id: 'languageStudio.callCenterNextStepsGetStarted.cognitiveResource',
        defaultMessage: 'Cognitive Services All-in-one resource'
    },
    languageResource: {
        id: 'languageStudio.callCenterNextStepsGetStarted.languageResource',
        defaultMessage: 'Language resource (Same service region as Speech)'
    },
    createResourceDesc: {
        id: 'languageStudio.callCenterNextStepsGetStarted.createResourceDesc',
        defaultMessage:
            'To run call center analytics, you’ll need Speech and Language resources. Sign in now if you already have an account, or sign up to create resources:'
    },
    quickStartDesc: {
        id: 'languageStudio.callCenterNextStepsGetStarted.quickStartDesc',
        defaultMessage: `Once you have created a Language resource, next set up the environment,
        install the Language SDK, and run sample code by following the steps in the quick start guide.`
    }
});

const PointContainer = styled.ul({ marginLeft: '10px' });
const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { marginLeft: 20 } });
const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { minHeight: '32px', height: 'auto' } });

export const CallCenterNextStepsGetStarted = ({ isResultsEmpty }: { isResultsEmpty: boolean }) => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const { isSignedIn } = rootStore.authStore;

    const handleClickLink = () => {
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    const handleSignInClick = () => {
        logDemoButtonClick('Call center', 'Nextsteps:GetStarted', 'SignIn');
        dispatcher.dispatch('login');
    };

    const handleCustomButtonClick = (buttonName: string) => {
        logDemoButtonClick('Call center', 'Nextsteps:GetStarted', buttonName);
        handleClickLink();
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.createResource)}
                </LuisText>
                <LuisText styles={textStyles}>
                    {localize(messages.createResourceDesc)}
                    <PointContainer>
                        <li>{localize(messages.speechResource)}</li>
                        <li>{localize(messages.languageResource)}</li>
                    </PointContainer>
                    <LuisText fontWeight={600}>{localize(messages.or)}</LuisText>
                    <PointContainer>
                        <li>{localize(messages.cognitiveResource)}</li>
                    </PointContainer>
                </LuisText>
            </Stack>

            {!isSignedIn && (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10, padding: '0 0 0 20px' }}>
                    <PrimaryButton styles={buttonStyles} onClick={handleSignInClick}>
                        {localize(messages.signInAzure)}
                    </PrimaryButton>
                    <LuisText>{localize(messages.or)}</LuisText>
                    <ExternalLink href="https://aka.ms/azure-freeaccount" onClick={() => handleCustomButtonClick('SignUp')}>
                        {localize(messages.signUpAzure)}
                    </ExternalLink>
                </Stack>
            )}

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.quickStart)}
                </LuisText>
                <LuisText styles={textStyles}>{localize(messages.quickStartDesc)}</LuisText>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 10, padding: '0 0 0 20px' }}>
                <PrimaryButton
                    target="_blank"
                    styles={buttonStyles}
                    text={localize(messages.openQuickStart)}
                    href="https://aka.ms/callcenter-overview"
                    onClick={() => handleCustomButtonClick('QuickStart')}
                />
                <DefaultButton
                    target="_blank"
                    styles={buttonStyles}
                    iconProps={{ iconName: 'Github' }}
                    text={localize(messages.viewSamples)}
                    href="https://aka.ms/language-callcenter-sample"
                    onClick={() => handleCustomButtonClick('GithubSamples')}
                />
            </Stack>
        </Stack>
    );
};
